import CachedRoundedIcon from "@mui/icons-material/CachedRounded";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader.tsx";
import biApi from "../../../../../../../../api/biApi.ts";
import type {
  CellDrillDownRef,
  CustomColumnDrillDownInfo,
  ReportField,
} from "../../../../../../../../api/types/svTypes.ts";
import { CellDrillDownInfoType } from "../../../../../../../../api/types/svTypes.ts";
import DataRefreshIcon from "@entrilia/shared/icons/DataRefreshIcon";
import {
  selectDrillDownCacheValid,
  selectEtag,
  selectSessionId,
} from "../../../../store/selectors/currentReportSelectors";
import type { DrillDownLayer, DrillDownPage } from "../../../../store/types/drillDownPageState.types";
import { createCurrentReportAction, createDrillDownPageAction } from "../../../../store/utils/storeUtils.ts";
import { formatFieldNumber, getFormulaCaptionsPreview, getFormulaValuesPreview } from "./utils/formulaPreviewHelper.ts";

interface Props {
  configuration: DrillDownPage;
}
export default function Loader({ configuration }: Props) {
  const dispatch = useDispatch();

  const sessionId = useSelector(selectSessionId);
  const isCacheValid = useSelector(selectDrillDownCacheValid);
  const [error, setError] = useState<boolean>(false);

  const validationFailed = useDrillDownCacheValidation();
  const layer = configuration.layer;
  const activeSubLayerIndex = layer.selectedSubLayerIndex;
  const activeLayer =
    layer.subLayers !== undefined && activeSubLayerIndex !== undefined ? layer.subLayers[activeSubLayerIndex] : layer;
  const activeDrillDownId = activeLayer?.drillDownId;
  const hasInfo = activeLayer?.info !== undefined;

  useEffect(() => {
    if (hasInfo || !activeDrillDownId || !isCacheValid) {
      return;
    }
    let cancelled = false;
    biApi
      .getDrillDownItem(sessionId, activeDrillDownId)
      .then((data) => {
        if (cancelled) {
          return;
        }
        if (data?.success !== true || !data.data.item || data.data.notFound !== false) {
          setError(true);
          return;
        }

        const combined = data.data.item.combined;
        if (combined.drillDownInfoType !== CellDrillDownInfoType.CustomColumn) {
          dispatch(
            createDrillDownPageAction("setInfo", {
              pageId: configuration.pageId,
              subLayerIndex: layer.subLayers !== undefined ? activeSubLayerIndex : undefined,
              info: data.data.item,
            })
          );
          return;
        }

        const customColumn = combined as CustomColumnDrillDownInfo;
        const subLayers = buildSubLayers(customColumn.drillDownRefs ?? [], configuration.layer.aggregationFields ?? []);
        const firstEnabled = subLayers.find((c) => !c.disabled);

        dispatch(
          createDrillDownPageAction("setSubLayers", {
            pageId: configuration.pageId,
            subLayers,
            formulaPreview: getFormulaCaptionsPreview(customColumn.formula, customColumn.drillDownRefs),
            formulaValuesPreview: getFormulaValuesPreview(customColumn.formula, customColumn.drillDownRefs),
          })
        );

        if (!firstEnabled?.drillDownId) {
          setError(true);
          return;
        }
        if (cancelled) {
          return;
        }

        const firstEnabledIndex = subLayers.indexOf(firstEnabled);
        return biApi.getDrillDownItem(sessionId, firstEnabled.drillDownId).then((componentData) => {
          if (cancelled) {
            return;
          }
          if (componentData?.success === true && componentData.data.item && !componentData.data.notFound) {
            dispatch(
              createDrillDownPageAction("setInfo", {
                pageId: configuration.pageId,
                subLayerIndex: firstEnabledIndex,
                info: componentData.data.item,
              })
            );
          } else {
            setError(true);
          }
        });
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setError(true);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [activeDrillDownId, configuration.pageId, activeSubLayerIndex, hasInfo, isCacheValid, sessionId]);

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", px: 3, pb: 3 }}>
      {!error && !validationFailed ? (
        <InlineLoader
          text="Preparing a reconciliation..."
          sx={{ width: "auto", height: "auto", textAlign: "center" }}
        />
      ) : (
        <Stack sx={{ alignItems: "center", justifyContent: "center", gap: 1 }}>
          <ErrorComponent />
        </Stack>
      )}
    </Box>
  );
}

function ErrorComponent() {
  const dispatch = useDispatch();

  const removeAllDrillDowns = useCallback(() => {
    dispatch(createDrillDownPageAction("removeAll"));
    dispatch(createCurrentReportAction("refresh"));
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  return (
    <Stack direction={"column"} sx={{ alignItems: "center", gap: 1, textAlign: "center" }}>
      <DataRefreshIcon />
      <Typography variant="h6">Outdated data!</Typography>
      <Typography color={"secondary"}>
        Unable to load data because it&apos;s outdated. Please try reloading the report and accessing the necessary data
        again.
      </Typography>
      <Button
        variant="contained"
        color="primary"
        sx={{ mt: 2 }}
        startIcon={<CachedRoundedIcon />}
        onClick={removeAllDrillDowns}
      >
        Reload Report
      </Button>
    </Stack>
  );
}

function buildSubLayers(drillDownRefs: CellDrillDownRef[], aggregationFields: ReportField[]): DrillDownLayer[] {
  return drillDownRefs.map((ref) => {
    const measure = aggregationFields.find((f) => f.config.guid === ref.fieldId);
    const formattedValue = ref.value != null ? formatFieldNumber(ref.value) : "";
    return {
      measure,
      cell: {
        value: ref.value ?? null,
        formattedValue,
        drillDownId: ref.drillDownId || undefined,
      },
      drillDownId: ref.drillDownId || undefined,
      functionName: ref.functionName,
      formattedValue: formattedValue || undefined,
      disabled: !ref.drillDownId,
    };
  });
}

function useDrillDownCacheValidation() {
  const dispatch = useDispatch();
  const sessionId = useSelector(selectSessionId);
  const etag = useSelector(selectEtag);
  const isCacheValid = useSelector(selectDrillDownCacheValid);
  const [error, setError] = useState<boolean>(false);

  const validateCache = useCallback(
    (isCancelled: () => boolean) => {
      const poll = () => {
        if (etag === undefined || isCancelled() === true) return;
        biApi
          .isDrillDownCacheValid(sessionId, etag)
          .then((data) => {
            if (isCancelled()) return;
            if (data.success && !data.data.expired) {
              if (data.data.isCacheValid) {
                dispatch(createCurrentReportAction("setEtag", { etag, cacheValid: true }));
              } else {
                setTimeout(() => poll(), 1500);
              }
            } else {
              dispatch(createCurrentReportAction("setEtag", { etag, cacheValid: false }));
              setError(true);
            }
          })
          .catch(() => {
            if (isCancelled()) return;
            dispatch(createCurrentReportAction("setEtag", { etag, cacheValid: false }));
            // eslint-disable-next-line @eslint-react/set-state-in-effect
            setError(true);
          });
      };
      poll();
    },
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [etag, sessionId]
  );

  useEffect(() => {
    if (isCacheValid && etag !== undefined) return;
    let cancelled = false;
    validateCache(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [etag, isCacheValid, sessionId, validateCache]);

  return error;
}
